import { Box, CircularProgress } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAllEnums } from '~/data/enums';
import { getLegalEntityName } from '~/data/legalEntity';
import { RehireDialog } from '~/features/RehireDialog/component';
import { ScheduleChanges } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/component';
import { jsSelectName } from '~/legacy-components/CustomDataFields';
import { LegacyFAIcon, LegacyFAIcons } from '~/legacy-components/LegacyFAIcon';
import {
  LegacyFieldReadMode,
  LegacyMuiButton,
} from '~/legacy-components/LegacyForms';
import { LegacyHeadingWithActions } from '~/legacy-components/LegacyLayout';
import { ScheduleHistory } from '~/legacy-components/ScheduleHistory';

import { EmploymentAllPersonnelGroups } from './components/EmploymentAllPersonnelGroups';
import { EmploymentDataAllCostCenters } from './components/EmploymentDataAllCostCenters';
import { EmploymentDataCostCenter } from './components/EmploymentDataCostCenter';
import { EmploymentDataTrialPeriod } from './components/EmploymentDataTrialPeriod/PendingEmploymentComponent';
import { EmploymentPendingLabel } from './components/EmploymentPendingLabel';
import { LabelType } from './components/EmploymentPendingLabel/component';
import { PendingEmploymentComponent } from './components/PendingEmploymentComponent';
import {
  getAllUnitPositionNames,
  getEmploymentFormattedDate,
  getFormattedEmploymentValue,
  hasPendingUnitPositionChanges,
} from './data';
import { useEmploymentDataForm } from './useEmploymentFormData';
import { useSxStyles } from './useStyles';

export const EmploymentDataForm = () => {
  const sxStyles = useSxStyles();
  const { t } = useTranslation();

  const {
    employeeId,
    addOverlappingEmployment,
    scheduleEmploymentChange,
    employmentHistory,
    rehireEmployee,
    employmentPositionColumn: {
      empPosition,
      empPositionPending,
      costCenters,
      legalEntities,
      newEmployment,

      pendingUnitName,
      positionCostCenters,
      getAllOrgUnitNames,

      relationsInfo: {
        empDeputyManager,
        hrResponsibilityUnits,
        managerResponsibilityUnits,
        alternateManagerResponsibilityUnits,
        getUnitNamesEmployeeIsResponsibleForCallback,
        getManagerName,
      },
      empBasicData,
    },
    employmentDetailsColumn: {
      pendingEmploymentDetails,
      employmentDetails,
      employmentData,
    },
    isLoading,
  } = useEmploymentDataForm();

  const { isNewEmp, unitPositions, pendingUnitPositions } = useMemo(() => {
    return {
      isNewEmp: !empPosition,
      unitPositions: empPosition?.fEmpUnitPositions ?? [],
      pendingUnitPositions: empPositionPending?.fEmpUnitPositions ?? [],
    };
  }, [empPosition, empPositionPending]);

  const { allEnums } = useAllEnums();

  return (
    <Box sx={sxStyles.root}>
      <Box sx={sxStyles.header}>
        <LegacyHeadingWithActions
          heading={t('laEmploymentData')}
          instruction={{
            title: t('laEmplInstr'),
            contentKey: 'INSTRUCTIONS_EMPLOYMENT',
          }}
        >
          {addOverlappingEmployment.isEnabled && (
            <LegacyMuiButton
              onClick={() => null}
              startIcon={<LegacyFAIcon FAIcon={LegacyFAIcons.Plus} />}
              disabled={true} // Disabled - until feature is built
            >
              {t('laAdd')}
            </LegacyMuiButton>
          )}

          {scheduleEmploymentChange.isEnabled && (
            <>
              {scheduleEmploymentChange.isLoading && (
                <LegacyMuiButton startIcon={<CircularProgress size={20} />}>
                  {t('laSchedule')}
                </LegacyMuiButton>
              )}
              {!scheduleEmploymentChange.isLoading && (
                <ScheduleChanges
                  handleCloseDialog={scheduleEmploymentChange.dialog.close}
                  handleOpenDialog={scheduleEmploymentChange.dialog.open}
                  open={scheduleEmploymentChange.dialog.isOpen}
                  empId={employeeId}
                />
              )}
            </>
          )}

          {employmentHistory.isEnabled && <ScheduleHistory />}

          {rehireEmployee.isEnabled && (
            <RehireDialog
              empId={employeeId}
              empBasicData={empBasicData}
              empPosition={empPosition}
              employmentDetails={employmentDetails}
              employmentData={employmentData}
            />
          )}
        </LegacyHeadingWithActions>
      </Box>
      <Box id="middle-column">
        {(empPositionPending || pendingEmploymentDetails || newEmployment) && (
          <Box id="pending-labels" sx={sxStyles.pendingLabels}>
            {empPositionPending && (
              <EmploymentPendingLabel
                labelType={LabelType.employment}
                labelValue={getEmploymentFormattedDate(
                  empPositionPending.fInternalPositionStartDate,
                )}
              />
            )}
            {pendingEmploymentDetails && (
              <EmploymentPendingLabel
                labelType={LabelType.employmentDetails}
                labelValue={getEmploymentFormattedDate(
                  pendingEmploymentDetails.fEmplDetailStartDate,
                )}
              />
            )}
            {newEmployment && empPosition?.fInternalPositionPosition && (
              <EmploymentPendingLabel
                labelType={LabelType.newEmployment}
                labelValue={`${jsSelectName(
                  allEnums.EMPLOYEE_POSITION,
                  empPosition.fInternalPositionPosition,
                )}, ${getEmploymentFormattedDate(newEmployment.startDate)}`}
              />
            )}
          </Box>
        )}
        <LegacyFieldReadMode
          label={t('fEmpContractNumber')}
          value={empBasicData.fEmpContractNumber || ''}
        />
        <LegacyFieldReadMode
          label={t('fEmpPersonNumber')}
          value={empBasicData.fEmpPersonNumber}
        />
        <LegacyFieldReadMode label={t('fEmpLegalEntity')}>
          {!!empPosition
            ? getLegalEntityName(empPosition?.fEmpLegalEntity, legalEntities)
            : ''}
          {empPositionPending && (
            <PendingEmploymentComponent
              pendingCondition={
                isNewEmp ||
                empPosition?.fEmpLegalEntity !==
                  empPositionPending.fEmpLegalEntity
              }
              pendingValue={getLegalEntityName(
                empPositionPending.fEmpLegalEntity,
                legalEntities,
              )}
            />
          )}
        </LegacyFieldReadMode>
        <LegacyFieldReadMode label={t('laPosition')}>
          {!!empPosition
            ? jsSelectName(
                allEnums.EMPLOYEE_POSITION,
                empPosition?.fInternalPositionPosition,
              )
            : ''}
          {empPositionPending && (
            <PendingEmploymentComponent
              pendingCondition={
                isNewEmp ||
                empPosition?.fInternalPositionPosition !==
                  empPositionPending.fInternalPositionPosition
              }
              pendingValue={jsSelectName(
                allEnums.EMPLOYEE_POSITION,
                empPositionPending.fInternalPositionPosition,
              )}
            />
          )}
        </LegacyFieldReadMode>
        <LegacyFieldReadMode label={t('fEmpJobTitle')}>
          {!!empPosition ? empPosition.fEmpJobTitle : ''}
          {empPositionPending && (
            <PendingEmploymentComponent
              pendingCondition={
                isNewEmp ||
                empPosition?.fEmpJobTitle !== empPositionPending.fEmpJobTitle
              }
              pendingValue={empPositionPending.fEmpJobTitle}
            />
          )}
        </LegacyFieldReadMode>
        <LegacyFieldReadMode label={t('fEmpBusinessUnits')}>
          {!!empPosition ? getAllOrgUnitNames(empPosition) : ''}
          {empPositionPending && pendingUnitName && (
            <PendingEmploymentComponent
              pendingCondition={
                isNewEmp ||
                empPosition?.fInternalPositionUnit !==
                  empPositionPending.fInternalPositionUnit
              }
              pendingValue={pendingUnitName}
            />
          )}
        </LegacyFieldReadMode>
        <LegacyFieldReadMode label={t('fEmpUnitPositions')}>
          {unitPositions.length > 0
            ? getAllUnitPositionNames(unitPositions)
            : ''}
          {pendingUnitPositions.length > 0 && (
            <PendingEmploymentComponent
              pendingCondition={
                isNewEmp ||
                hasPendingUnitPositionChanges(
                  unitPositions,
                  pendingUnitPositions,
                )
              }
              pendingValue={getAllUnitPositionNames(unitPositions)}
            />
          )}
        </LegacyFieldReadMode>
        <LegacyFieldReadMode label={t('fEmpCostCenter')}>
          {!!positionCostCenters.current.primary ? (
            <EmploymentDataCostCenter
              costCenterId={positionCostCenters.current.primary.fEmpCostCenter}
              percentage={
                positionCostCenters.current.primary.fEmpCostCenterPercent
              }
              hasCustomFields={positionCostCenters.current.hasCustomDataFields}
              costCenters={costCenters}
            ></EmploymentDataCostCenter>
          ) : (
            ''
          )}
          {empPositionPending &&
            !!positionCostCenters.current.primary &&
            !!positionCostCenters.pending.primary && (
              <PendingEmploymentComponent
                pendingCondition={
                  isNewEmp ||
                  positionCostCenters.current.primary.fEmpCostCenter !==
                    positionCostCenters.pending.primary.fEmpCostCenter ||
                  positionCostCenters.current.primary.fEmpCostCenterPercent !==
                    positionCostCenters.pending.primary.fEmpCostCenterPercent
                }
                pendingValue={
                  <EmploymentDataCostCenter
                    costCenterId={
                      positionCostCenters.pending.primary.fEmpCostCenter
                    }
                    percentage={
                      positionCostCenters.pending.primary.fEmpCostCenterPercent
                    }
                    hasCustomFields={
                      positionCostCenters.pending.hasCustomDataFields
                    }
                    costCenters={costCenters}
                  ></EmploymentDataCostCenter>
                }
              />
            )}
        </LegacyFieldReadMode>
        {!!empPosition &&
          ((positionCostCenters.current.additional &&
            positionCostCenters.current.additional.length > 0) ||
            (empPositionPending &&
              positionCostCenters.pending.additional &&
              positionCostCenters.pending.additional.length > 0)) && (
            <LegacyFieldReadMode label={t('fEmpAddlCostCenters')}>
              {!!empPosition ? (
                <EmploymentDataAllCostCenters
                  empCostCenters={positionCostCenters.current.additional}
                  costCenters={costCenters}
                />
              ) : (
                ''
              )}
              {empPositionPending && (
                <PendingEmploymentComponent
                  pendingCondition={
                    isNewEmp ||
                    positionCostCenters.pending.additional.length !==
                      positionCostCenters.current.additional.length ||
                    positionCostCenters.pending.additional.some(
                      (pcc) =>
                        !!positionCostCenters.current.additional.find(
                          (acc) => acc.fEmpCostCenter !== pcc.fEmpCostCenter,
                        ),
                    )
                  }
                  pendingValue={
                    <EmploymentDataAllCostCenters
                      empCostCenters={positionCostCenters.pending.additional}
                      costCenters={costCenters}
                    />
                  }
                />
              )}
            </LegacyFieldReadMode>
          )}
        <LegacyFieldReadMode label={t('fEmpManager')}>
          {!!empPosition
            ? getManagerName(empPosition.fInternalPositionManager)
            : ''}
          {empPositionPending && (
            <PendingEmploymentComponent
              pendingCondition={
                isNewEmp ||
                empPosition.fInternalPositionManager !==
                  empPositionPending.fInternalPositionManager
              }
              pendingValue={getManagerName(
                empPositionPending.fInternalPositionManager,
              )}
            />
          )}
        </LegacyFieldReadMode>
        {empDeputyManager && (
          <LegacyFieldReadMode label={t('laDeputyManager')}>
            {empDeputyManager ? getManagerName(empDeputyManager) : ''}
          </LegacyFieldReadMode>
        )}
        {managerResponsibilityUnits?.length > 0 && !isLoading && (
          <LegacyFieldReadMode label={t('laMgrResponsibility')}>
            {getUnitNamesEmployeeIsResponsibleForCallback(
              managerResponsibilityUnits,
            )}
          </LegacyFieldReadMode>
        )}
        {alternateManagerResponsibilityUnits?.length > 0 && !isLoading && (
          <LegacyFieldReadMode label={t('laAltMgrResponsibility')}>
            {getUnitNamesEmployeeIsResponsibleForCallback(
              alternateManagerResponsibilityUnits,
            )}
          </LegacyFieldReadMode>
        )}
        {hrResponsibilityUnits?.length > 0 && (
          <LegacyFieldReadMode label={t('fHRResponsibilityOver')}>
            {getUnitNamesEmployeeIsResponsibleForCallback(
              hrResponsibilityUnits,
            )}
          </LegacyFieldReadMode>
        )}
      </Box>
      <Box id="right-column">
        <LegacyFieldReadMode
          label={t('laEmploymentType')}
          value={getFormattedEmploymentValue(employmentData?.fEmplType)}
        />
        <LegacyFieldReadMode label={t('laEmploymentStatus')}>
          {getFormattedEmploymentValue(
            employmentDetails?.fEmplDetailStatus,
            '_',
            '-',
          )}
          {employmentDetails && pendingEmploymentDetails && (
            <PendingEmploymentComponent
              pendingCondition={
                isNewEmp ||
                employmentDetails.fEmplDetailStatus !==
                  pendingEmploymentDetails.fEmplDetailStatus
              }
              pendingValue={getFormattedEmploymentValue(
                pendingEmploymentDetails.fEmplDetailStatus,
                '_',
                '-',
              )}
            />
          )}
        </LegacyFieldReadMode>
        <LegacyFieldReadMode
          label={t('fCurrency')}
          value={employmentData?.fEmplCurrency ?? ''}
        />
        <LegacyFieldReadMode
          label={t('fEmplDateStart')}
          value={getEmploymentFormattedDate(employmentData?.fEmplDateStart)}
        />
        <LegacyFieldReadMode
          label={t('fArrivalDate')}
          value={getEmploymentFormattedDate(employmentData?.fEmplDateArrival)}
        />
        <LegacyFieldReadMode label={t('fTrialPeriod')}>
          {employmentData?.fEmplTrialPeriod ? (
            <EmploymentDataTrialPeriod
              code={employmentData?.fEmplTrialPeriod}
            />
          ) : (
            ''
          )}
        </LegacyFieldReadMode>
        <LegacyFieldReadMode
          label={t('fTrialPeriodEndDateInputLabel')}
          value={getEmploymentFormattedDate(
            employmentData?.fEmplTrialPeriodEndDate,
          )}
        />
        <LegacyFieldReadMode
          label={t('fEmplPlannedEndDate')}
          value={getEmploymentFormattedDate(
            employmentData?.fEmplPlannedEndDate,
          )}
        />
        <LegacyFieldReadMode
          label={t('fLastDEmpl')}
          value={getEmploymentFormattedDate(
            employmentData?.fEmplLastDateEmployment,
          )}
        />
        <LegacyFieldReadMode
          label={t('fLastDTemp')}
          value={getEmploymentFormattedDate(
            employmentData?.fEmplTerminationDate,
          )}
        />
        <LegacyFieldReadMode
          label={t('fTerminationReasonInputLabel')}
          value={getFormattedEmploymentValue(
            employmentData?.fEmplTerminationReason,
            '_',
            ' ',
          )}
        />
        <LegacyFieldReadMode
          label={t('fReasonFixTerm')}
          value={getFormattedEmploymentValue(
            employmentData?.fEmplFixedTermReason,
          )}
        />
        <LegacyFieldReadMode label={t('fJobGrade')}>
          {getFormattedEmploymentValue(
            employmentDetails?.fEmplDetailJobGrade,
            '_',
            ' ',
          )}
          {employmentDetails &&
            pendingEmploymentDetails?.fEmplDetailJobGrade && (
              <PendingEmploymentComponent
                pendingCondition={
                  isNewEmp ||
                  employmentDetails.fEmplDetailJobGrade !==
                    pendingEmploymentDetails.fEmplDetailJobGrade
                }
                pendingValue={getFormattedEmploymentValue(
                  pendingEmploymentDetails.fEmplDetailJobGrade,
                  '_',
                  ' ',
                )}
                isColorGreen
              />
            )}
        </LegacyFieldReadMode>
        <LegacyFieldReadMode label={t('fTaxNumberInputLabel')}>
          {employmentDetails?.fEmplDetailTaxNumber ?? ''}
          {employmentDetails &&
            pendingEmploymentDetails?.fEmplDetailTaxNumber && (
              <PendingEmploymentComponent
                pendingCondition={
                  isNewEmp ||
                  employmentDetails.fEmplDetailTaxNumber !==
                    pendingEmploymentDetails.fEmplDetailTaxNumber
                }
                pendingValue={pendingEmploymentDetails.fEmplDetailTaxNumber}
                isColorGreen
              />
            )}
        </LegacyFieldReadMode>
        <LegacyFieldReadMode label={t('fVacationBalanceCalculationInputLabel')}>
          {`${employmentDetails?.fEmplDetailVacBalCalcSixDays ?? 5} ${t(
            'laDays',
          )}`}
          {employmentDetails &&
            pendingEmploymentDetails?.fEmplDetailVacBalCalcSixDays && (
              <PendingEmploymentComponent
                pendingCondition={
                  isNewEmp ||
                  employmentDetails.fEmplDetailVacBalCalcSixDays !==
                    pendingEmploymentDetails.fEmplDetailVacBalCalcSixDays
                }
                pendingValue={`${
                  pendingEmploymentDetails.fEmplDetailVacBalCalcSixDays
                } ${t('laDays')}`}
                isColorGreen
              />
            )}
        </LegacyFieldReadMode>
        <EmploymentAllPersonnelGroups
          employmentDetails={employmentDetails}
          pendingEmploymentDetails={pendingEmploymentDetails}
        />
        <LegacyFieldReadMode label={t('fCalculateHoursBalanceInputLabel')}>
          {employmentDetails?.fEmplDetailCalcHoursBalance
            ? t('laYes')
            : t('laNo')}
          {employmentDetails &&
            pendingEmploymentDetails?.fEmplDetailCalcHoursBalance && (
              <PendingEmploymentComponent
                pendingCondition={
                  isNewEmp ||
                  employmentDetails.fEmplDetailCalcHoursBalance !==
                    pendingEmploymentDetails.fEmplDetailCalcHoursBalance
                }
                pendingValue={
                  pendingEmploymentDetails.fEmplDetailCalcHoursBalance
                    ? t('laYes')
                    : t('laNo')
                }
                isColorGreen
              />
            )}
        </LegacyFieldReadMode>
        <LegacyFieldReadMode
          label={t('fEmplInfo')}
          value={employmentData?.fEmplInfo ?? ''}
        />
      </Box>
    </Box>
  );
};
